import { supabase } from './supabase';

/**
 * Suivi des DEMANDES d'options / formules (table `option_requests`, cf. sql/57).
 *  - côté resto : savoir ce qui est « en cours de demande » (badge 🟡),
 *  - côté super admin : lister les demandes en attente + activer (Edge activate-option).
 * RÉSILIENT : si la table n'existe pas encore, renvoie vide sans casser l'UI.
 */
export interface OptionRequest {
  id: string;
  tenantId: string;
  tenantSlug: string | null;
  kind: string; // option | plan
  reqKey: string;
  label: string;
  status: string; // pending | done | rejected
  createdAt: string;
}

function mapRow(r: Record<string, unknown>): OptionRequest {
  return {
    id: r.id as string,
    tenantId: r.tenant_id as string,
    tenantSlug: (r.tenant_slug ?? null) as string | null,
    kind: (r.kind ?? 'option') as string,
    reqKey: r.req_key as string,
    label: r.label as string,
    status: (r.status ?? 'pending') as string,
    createdAt: r.created_at as string,
  };
}

/** Clés (module ou formule) EN ATTENTE pour un resto → badge « demande en cours ». */
export async function loadTenantPendingKeys(tenantId: string): Promise<Set<string>> {
  const { data, error } = await supabase
    .from('option_requests')
    .select('req_key')
    .eq('tenant_id', tenantId)
    .eq('status', 'pending');
  if (error || !Array.isArray(data)) return new Set();
  return new Set(data.map((r) => (r as { req_key: string }).req_key));
}

/** Toutes les demandes en attente (super admin). */
export async function loadAllPendingRequests(): Promise<OptionRequest[]> {
  const { data, error } = await supabase
    .from('option_requests')
    .select('id, tenant_id, tenant_slug, kind, req_key, label, status, created_at')
    .eq('status', 'pending')
    .order('created_at', { ascending: false });
  if (error || !Array.isArray(data)) {
    if (error) console.warn('[option_requests] lecture impossible:', error.message);
    return [];
  }
  return data.map(mapRow);
}

/** Active une demande (super admin) via l'Edge `activate-option` : pose les flags + notifie le client. */
export async function activateRequest(input: {
  tenantId: string;
  tenantEmail?: string | null;
  featureKeys: string[];
  label: string;
  requestId?: string;
}): Promise<{ ok: boolean; error?: string }> {
  const { data, error } = await supabase.functions.invoke('activate-option', {
    body: {
      tenant_id: input.tenantId,
      tenant_email: input.tenantEmail ?? '',
      feature_keys: input.featureKeys,
      label: input.label,
      request_id: input.requestId ?? '',
    },
  });
  if (error) return { ok: false, error: error.message };
  const res = (data ?? {}) as { ok?: boolean; error?: string };
  if (!res.ok) return { ok: false, error: res.error ?? 'Activation impossible.' };
  return { ok: true };
}
